import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Typography, Spin, Alert, Card, Tag,
  Button, Input, Radio, Checkbox, Space,
} from "antd";
import { ConfigProvider } from "antd";
import Navbar from "../../../components/shared/navbar/Navbar";
import CustomBreadcrumb from "../../../components/shared/breadcrumbs/CustomBreadCrumb";
import CustomButton from "../../../components/global/Button/CustomButton";
import Back from "../../../assets/back-btn.svg"
import { useQuizAttempt, formatTime } from "../../../hooks/UserHooks/MyCoursesHook/useQuizAttempt";
import "./CourseQuizAttempt.css";

const { Title, Paragraph, Text } = Typography;


const QuizStats = ({ quiz }) => (
  <div className="quiz-stats-container">
    <div className="stat-item">
      <div className="stat-value">
        {quiz.timeLimit != null ? `${quiz.timeLimit} Minutes` : "No"}
      </div>
      <div className="stat-label">Time Limit</div>
    </div>
    <div className="stat-divider" />
    <div className="stat-item">
      <div className="stat-value">{quiz.attemptsAllowed}</div>
      <div className="stat-label">Attempts Allowed</div>
    </div>
    <div className="stat-divider" />
    <div className="stat-item">
      <div className="stat-value">{quiz.remainingAttempts}</div>
      <div className="stat-label">Remaining Attempts</div>
    </div>
    <div className="stat-divider" />
    <div className="stat-item">
      <div className="stat-value">
        {quiz.passingScore != null ? `${quiz.passingScore}%` : "N/A"}
      </div>
      <div className="stat-label">Passing Score</div>
    </div>
  </div>
);

const Timer = ({ timeLeft, timeLimit }) => (
  <div className="timer-container">
    <svg className="timer-svg" width="60" height="60">
      <circle cx="30" cy="30" r="27" className="timer-background-circle" />
      <circle
        cx="30" cy="30" r="27"
        className="timer-progress-circle"
        strokeDasharray={`${2 * Math.PI * 27}`}
        strokeDashoffset={`${2 * Math.PI * 27 * (1 - timeLeft / (timeLimit * 60))}`}
      />
    </svg>
    <div className="timer-text">{formatTime(timeLeft)}</div>
  </div>
);

const QuestionNavPanel = ({ questions, userAnswers, markedForReview, visited, currentIndex, setCurrentIndex }) => (
  <div className="question-nav-panel">
    <div className="question-nav-container">
      <div className="question-grid-wrapper">
        <div className="question-grid">
          {questions.map((q, index) => {
            const isAnswered =
              userAnswers[q.questionId] !== undefined &&
              userAnswers[q.questionId] !== "" &&
              userAnswers[q.questionId] !== null &&
              (Array.isArray(userAnswers[q.questionId])
                ? userAnswers[q.questionId].length > 0
                : true);
            const isMarked = markedForReview[q.questionId];
            const isCurrent = index === currentIndex;

            let btnClass = "question-button question-button-not-visited";
            if (isMarked) btnClass = "question-button question-button-marked";
            else if (isAnswered) btnClass = "question-button question-button-answered";
            else if (visited[q.questionId]) btnClass = "question-button question-button-not-answered";
            if (isCurrent) btnClass += " question-button-current";

            return (
              <Button
                key={q.questionId}
                onClick={() => setCurrentIndex(index)}
                className={btnClass}
              >
                Q{index + 1}.
              </Button>
            );
          })}
        </div>
      </div>

      <div className="legend-container">
        <div className="legend-item">
          <div className="legend-dot legend-dot-not-answered" />
          <span>Not Answered</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot legend-dot-answered" />
          <span>Answered</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot legend-dot-marked" />
          <span>Marked For Review</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot legend-dot-not-visited" />
          <span>Not Visited</span>
        </div>
      </div>
    </div>
  </div>
);

const QuestionArea = ({ currentQuestion, currentIndex, userAnswers, markedForReview, handleAnswerChange }) => {
  const options = JSON.parse(currentQuestion?.options || "[]");

  return (
    <div className="question-section">
      <div className="question-header">
        <div className="question-title-row">
          <Title level={5} className="question-title">
            Question {currentIndex + 1}.
          </Title>
          {markedForReview[currentQuestion?.questionId] && (
            <Tag color="purple" className="review-badge">Marked for review</Tag>
          )}
        </div>
        <Paragraph className="question-text">{currentQuestion?.questionText}</Paragraph>
      </div>

      <ConfigProvider theme={{ token: { colorPrimary: "#ff4d4f", colorPrimaryHover: "#ff7875" } }}>
        {currentQuestion?.questionType === "SHORT_ANSWER" && (
          <Input.TextArea
            rows={4}
            placeholder="Type your answer here..."
            value={userAnswers[currentQuestion.questionId] || ""}
            onChange={(e) => handleAnswerChange(currentQuestion.questionId, e.target.value)}
            className="answer-textarea"
          />
        )}

        {currentQuestion?.questionType === "MCQ_SINGLE" && (
          <Radio.Group
            onChange={(e) => handleAnswerChange(currentQuestion.questionId, e.target.value)}
            value={userAnswers[currentQuestion.questionId]}
            className="answer-radio-group"
          >
            <Space direction="vertical" className="answer-space">
              {options.map((opt) => (
                <Radio key={opt} value={opt} className="answer-option">{opt}</Radio>
              ))}
            </Space>
          </Radio.Group>
        )}

        {currentQuestion?.questionType === "MCQ_MULTIPLE" && (
          <Checkbox.Group
            onChange={(checked) => handleAnswerChange(currentQuestion.questionId, checked)}
            value={userAnswers[currentQuestion.questionId] || []}
            className="answer-checkbox-group"
          >
            <Space direction="vertical" className="answer-space">
              {options.map((opt) => (
                <Checkbox key={opt} value={opt} className="answer-option">{opt}</Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        )}
      </ConfigProvider>
    </div>
  );
};


const CourseQuizAttempt = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const {
    quiz,
    questions,
    currentQuestion,
    currentIndex,
    setCurrentIndex,
    userAnswers,
    markedForReview,
    visited,
    loading,
    start,
    isAcknowledged,
    setIsAcknowledged,
    timeLeft,
    error,
    handleStartQuiz,
    confirmSubmit,
    handleAnswerChange,
    toggleMarkForReview,
    handleNext,
    handleBack,
    ConfirmModal,
  } = useQuizAttempt(courseId);

  if (loading) return <Spin fullscreen tip="Loading quiz..." />;
  if (error) return <Alert type="error" message={error} showIcon />;
  if (!quiz) return <Alert type="info" message="No quiz found." showIcon />;

  return (
    <div>
      <Navbar />

      {/* Breadcrumb */}
      <div className="breadcrumb-section">
        <div className="breadcrumb-wrapper">
          <img src={Back} onClick={() => navigate(-1)} alt="Back" className="back-button" />
          <CustomBreadcrumb 
          items={[
            { title: <Link to="/user">Home</Link> },
            { title: <Link to="/my-courses">Courses</Link> },
            { title: <Link to={`/my-courses/course-content-user/${courseId}`}>Content</Link> },
            { title: "Attempt Quiz" }
          ]} />
        </div>
      </div>

      <div className="quiz-container">
        <Card bordered className="quiz-card">

          {!start ? (
            <div className="quiz-start-container">
              <div className="quiz-info-section">
                <div className="quiz-title-section">
                  <Title className="quiz-title">{quiz.title}</Title>
                  {quiz.description && (
                    <>
                      <Text type="secondary" className="quiz-description-label">
                        Quiz Description
                      </Text>
                      <Paragraph className="quiz-description">{quiz.description}</Paragraph>
                    </>
                  )}
                </div>
                <QuizStats quiz={quiz} />
              </div>

              <div>
                <Paragraph className="disclaimer-text">
                  Note: After starting the quiz you cannot pause it, leaving or changing tab will submit the quiz.
                </Paragraph>
                <ConfigProvider
                  theme={{
                    components: {
                      Checkbox: { colorPrimary: "#ff4d4f", colorPrimaryHover: "#ff7875" },
                    },
                  }}
                >
                  <Checkbox
                    checked={isAcknowledged}
                    onChange={(e) => setIsAcknowledged(e.target.checked)}
                  >
                    Acknowledged
                  </Checkbox>
                </ConfigProvider>
              </div>
            </div>

          ) : (
            <div className="quiz-body-flex">

              <div className="question-main-wrapper">
                <div className="quiz-header">
                  <Title level={2} className="quiz-header-title">{quiz.title}</Title>
                  <Timer timeLeft={timeLeft} timeLimit={quiz.timeLimit} />
                </div>

                <div className="quiz-content-area">
                  <div className="question-main-col">
                    <QuestionArea
                      currentQuestion={currentQuestion}
                      currentIndex={currentIndex}
                      userAnswers={userAnswers}
                      markedForReview={markedForReview}
                      handleAnswerChange={handleAnswerChange}
                    />

                    <div className="navigation-buttons">
                      <CustomButton
                        variant="outlined"
                        size="large"
                        onClick={handleBack}
                        disabled={currentIndex === 0}
                        className="nav-button-previous"
                      >
                        Previous
                      </CustomButton>

                      <CustomButton
                        variant="default"
                        size="large"
                        onClick={() => toggleMarkForReview(currentQuestion.questionId)}
                        className="nav-button-mark"
                      >
                        {markedForReview[currentQuestion?.questionId] ? "Unmark review" : "Mark for review"}
                      </CustomButton>

                      <CustomButton
                        variant="primary"
                        size="large"
                        onClick={handleNext}
                        disabled={currentIndex === questions.length - 1}
                        className="nav-button-next"
                      >
                        Next
                      </CustomButton>

                      {currentIndex === questions.length - 1 && (
                        <CustomButton
                          variant="outlined"
                          size="large"
                          onClick={confirmSubmit}
                          className="nav-button-submit"
                        >
                          Submit Quiz
                        </CustomButton>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <QuestionNavPanel
                questions={questions}
                userAnswers={userAnswers}
                markedForReview={markedForReview}
                visited={visited}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
              />
            </div>
          )}
        </Card>

        {!start && (
          <div className="start-button-row">
            <CustomButton
              variant="primary"
              className={`start-button ${isAcknowledged ? "start-button-enabled" : "start-button-disabled"}`}
              onClick={handleStartQuiz}
              disabled={!isAcknowledged}
            >
              Start Quiz
            </CustomButton>
          </div>
        )}
      </div>
      {ConfirmModal}
    </div>
  );
};

export default CourseQuizAttempt;